import React, { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { message, UploadFile } from 'antd';
import { Action, FileIds, FileSlots, Props, Slot, Target } from './types';
import { useTranslation } from 'react-i18next';

const ProgressUtils = {
  normalize(value: number | undefined): number | undefined {
    if (typeof value !== 'number') return undefined;
    return Math.max(0, Math.min(100, Math.round(value)));
  },
};

const ImageUtils = {
  isImageLike(file?: UploadFile): boolean {
    const name = file?.name?.toLowerCase() ?? '';
    const mime = (file?.type as string) ?? '';
    return mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
  },

  createThumbnailUrl(file?: UploadFile): string | undefined {
    if (!file) return undefined;

    if (file.originFileObj && this.isImageLike(file)) {
      return URL.createObjectURL(file.originFileObj as File);
    }
    if (file.thumbUrl) return file.thumbUrl;
    if (file.url && this.isImageLike(file)) return file.url;
    
    return undefined;
  },
};

export function useOptionalFormContext() {
  try {
    return useFormContext();
  } catch {
    return undefined;
  }
}

export function useWarningThrottle() {
  const warnFlags = useRef<Record<string, boolean>>({});

  return useCallback((key: string, msg: string, timeoutMs = 1000) => {
    if (warnFlags.current[key]) return;
    
    warnFlags.current[key] = true;
    message.warning(msg);
    
    setTimeout(() => {
      warnFlags.current[key] = false;
    }, timeoutMs);
  }, []);
}

export function useThumbnailCache() {
  const thumbs = useRef<{ front?: string; back?: string }>({});
  const [thumbTick, forceThumbTick] = React.useState(0);

  const updateThumbFor = useCallback((slot: Slot, file?: UploadFile) => {
    const prevUrl = thumbs.current[slot];
    const newUrl = ImageUtils.createThumbnailUrl(file);

    if (prevUrl && prevUrl.startsWith('blob:') && prevUrl !== newUrl) {
      URL.revokeObjectURL(prevUrl);
    }

    thumbs.current[slot] = newUrl;
    forceThumbTick((t) => t + 1);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(thumbs.current).forEach((url) => {
        if (url?.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  return { thumbs, thumbTick, updateThumbFor };
}

function useProgressWithRaf() {
  const rafRef = useRef<{ front?: number; back?: number }>({});

  const setProgress = useCallback((slot: Slot, value: number | undefined) => {
    if (rafRef.current[slot]) {
      cancelAnimationFrame(rafRef.current[slot]!);
    }

    rafRef.current[slot] = requestAnimationFrame(() => {
      // This will be handled by the dispatcher passed from the component
    });

    return ProgressUtils.normalize(value);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(rafRef.current).forEach((rafId) => {
        if (rafId) cancelAnimationFrame(rafId);
      });
    };
  }, []);

  return setProgress;
}

// ============================================================================
// Component Logic Hooks
// ============================================================================
export function useFileUpload(
  uploadFile: Props['uploadFile'],
  dispatch: React.Dispatch<Action>,
  setIds: (ids: Partial<FileIds>) => void
) {
  const setProgressBase = useProgressWithRaf();
  const { t } = useTranslation()

  const setProgress = useCallback(
    (slot: Slot, value: number | undefined) => {
      const normalized = setProgressBase(slot, value);
      dispatch({ type: 'SET_PROGRESS', slot, value: normalized });
    },
    [setProgressBase, dispatch]
  );

  const doUpload = useCallback(
    async (slot: Slot, fileObj: File) => {
      try {
        dispatch({ type: 'SET_UPLOADING', slot, value: true });
        setProgress(slot, 0);

        const { id } = await uploadFile(fileObj, slot, (p) => setProgress(slot, p));

        setProgress(slot, 100);
        dispatch({ type: 'SET_UPLOAD_INPUT', value: true });
        setIds({ [slot]: id });

        setTimeout(() => setProgress(slot, undefined), 600);
      } catch (error) {
        message.error(t('businessCardOrders.idcard.uploadError'));
        setProgress(slot, undefined);
        setIds({ [slot]: undefined });
      } finally {
        dispatch({ type: 'SET_UPLOADING', slot, value: false });
      }
    },
    [uploadFile, setProgress, setIds, dispatch]
  );

  return { doUpload, setProgress };
}

export function useFileAssignment(
  slots: FileSlots,
  warnOnce: (key: string, msg: string) => void
) {
  const { t } = useTranslation()

  const assignFiles = useCallback(
    (newFiles: UploadFile[], target: Target) => {
      type Assignment = { slot: Slot; file: UploadFile };
      const assignments: Assignment[] = [];

      if (target) {
        // Specific slot targeted
        if (newFiles[0]) {
          assignments.push({ slot: target, file: newFiles[0] });
        }

        const otherSlot: Slot = target === 'front' ? 'back' : 'front';
        if (!slots[otherSlot] && newFiles[1]) {
          assignments.push({ slot: otherSlot, file: newFiles[1] });
        }

        if (newFiles.length > assignments.length) {
          warnOnce('count', t('businessCardOrders.idcard.maxFileCount'));
        }
      } else if (!slots.front && !slots.back) {
        // Both slots empty
        if (newFiles[0]) {
          assignments.push({ slot: 'front', file: newFiles[0] });
        }
        if (newFiles[1]) {
          assignments.push({ slot: 'back', file: newFiles[1] });
        }

        if (newFiles.length > 2) {
          warnOnce('count', t('businessCardOrders.idcard.maxFileCount'));
        }
      } else {
        // One slot filled
        const firstNew = newFiles[0];
        if (!slots.front) {
          assignments.push({ slot: 'front', file: firstNew });
        } else if (!slots.back) {
          assignments.push({ slot: 'back', file: firstNew });
        }

        if (newFiles.length > 1) {
          warnOnce('count', t('businessCardOrders.idcard.maxFileCount'));
        }
      }

      return assignments;
    },
    [slots, warnOnce]
  );

  return assignFiles;
}